import { useEffect, useMemo, useState } from "react";
import { TrendingUp, Mail } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

const COLORS = ["#f59e0b", "#059669", "#3b82f6", "#e11d48", "#8b5cf6", "#14b8a6", "#f97316", "#84cc16"];
const CHART_MARGIN = { top: 8, right: 16, left: -16, bottom: 0 };
const TICK = { fontSize: 11, fill: "#94a3b8" };
const TOOLTIP_STYLE = { background: "#0f172a", border: "1px solid #334155", fontSize: 12 };
const LEGEND_STYLE = { fontSize: 12 };
const rankFormatter = (v) => [`${v}° posto`];
const shortMonth = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("it-IT", { month: "short", year: "2-digit" });
};

export function StoreRankHistory({ contestId, isAdmin }) {
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    setData(null);
    api.storeHistory(12, contestId).then(setData).catch(() => setData({ stores: [], points: [] }));
  }, [contestId]);

  const sendNow = async () => {
    if (!window.confirm("Inviare ora agli admin l'email con la classifica del mese scorso?")) return;
    setSending(true);
    try {
      const r = await api.sendMonthlyStoreReport();
      toast.success(r.winner ? `Email inviata: ${r.winner.store_name} è il punto vendita di ${r.month}` : `Email inviata (nessuna giocata a ${r.month})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito");
    } finally {
      setSending(false);
    }
  };

  const maxRank = Math.max(1, data?.stores?.length || 1);
  const yDomain = useMemo(() => [1, maxRank], [maxRank]);
  const points = useMemo(() => (data?.points || []).map((p) => ({ ...p, label: shortMonth(p.month) })), [data]);
  return (
    <div className="mt-5 pt-5 border-t border-border" data-testid="store-rank-history">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="flex-1 min-w-[220px]">
          <h4 className="font-semibold text-sm flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" /> Andamento posizioni · ultimi 12 mesi</h4>
          <p className="text-xs text-muted-foreground">Posizione in classifica di ogni sede mese per mese (1 = prima). Snapshot salvato automaticamente il 1° del mese.</p>
        </div>
        {isAdmin && (
          <Button data-testid="monthly-report-send-button" variant="outline" size="sm" className="gap-2 border-border" onClick={sendNow} disabled={sending}>
            <Mail className="h-4 w-4" /> {sending ? "Invio…" : "Invia email sede del mese"}
          </Button>
        )}
      </div>
      {!data ? (
        <p className="text-sm text-muted-foreground">Caricamento…</p>
      ) : data.stores.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="history-empty">Nessun dato storico disponibile.</p>
      ) : (
        <div className="h-64" data-testid="history-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={CHART_MARGIN}>
              <CartesianGrid stroke="rgba(148,163,184,0.15)" vertical={false} />
              <XAxis dataKey="label" tick={TICK} />
              <YAxis reversed domain={yDomain} allowDecimals={false} tickCount={maxRank} tick={TICK} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={rankFormatter} />
              <Legend wrapperStyle={LEGEND_STYLE} />
              {data.stores.map((s, i) => (
                <Line key={s.id} type="monotone" dataKey={s.id} name={s.name} stroke={COLORS[i % COLORS.length]} strokeWidth={2.5} dot={{ r: 4 }} connectNulls={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
